import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Card,
  Col,
  Row,
  Typography,
} from "antd";

import {
  AimOutlined,
  BookOutlined,
  CheckCircleOutlined,
  CrownOutlined,
  GlobalOutlined,
  MailOutlined,
  ReadOutlined,
  RocketOutlined,
  SafetyOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";

import { motion } from "framer-motion";
import ProfileImage from "../NewDashboard/ProfileImage";

const {
  Title,
  Text,
  Paragraph,
} = Typography;

const StudentOverview = ({ theme }) => {
  const darkMode = theme?.darkMode;

  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const loadUser = () => {
      try {
        const possibleKeys = [
          "user",
          "currentUser",
          "authUser",
          "profile",
        ];

        let foundUser = null;

        for (const key of possibleKeys) {
          const stored = localStorage.getItem(key);

          if (!stored) {
            continue;
          }

          try {
            const parsed = JSON.parse(stored);

            if (
              parsed &&
              typeof parsed === "object"
            ) {
              foundUser = parsed;
              break;
            }
          } catch {
            continue;
          }
        }

        setCurrentUser(foundUser);
      } catch {
        setCurrentUser(null);
      }
    };

    loadUser();

    window.addEventListener(
      "user-logged-in",
      loadUser
    );

    window.addEventListener(
      "storage",
      loadUser
    );

    return () => {
      window.removeEventListener(
        "user-logged-in",
        loadUser
      );

      window.removeEventListener(
        "storage",
        loadUser
      );
    };
  }, []);

  const colors = useMemo(
    () => ({
      navy: "#062A4B",
      deepBlue: "#063A6B",
      sky: "#0F6DAB",
      gold: "#F6C944",
      antique: "#CBA454",
      orange: "#C0750D",
      green: "#4FAE22",
      teal: "#167A89",

      page: darkMode
        ? "#0D1522"
        : "#F5F7FB",

      card: darkMode
        ? "#182236"
        : "#FFFFFF",

      soft: darkMode
        ? "#121D2E"
        : "#F8FAFD",

      text: darkMode
        ? "#F3F6FA"
        : "#142536",

      muted: darkMode
        ? "#9BA9BC"
        : "#667085",

      border: darkMode
        ? "#2B3951"
        : "#E1E6EF",
    }),
    [darkMode]
  );

  const studentName =
    currentUser?.username ||
    currentUser?.name ||
    currentUser?.full_name ||
    currentUser?.fullName ||
    "Student";

  const studentEmail =
    currentUser?.email ||
    "";

  const rawUserType =
    currentUser?.user_type ||
    currentUser?.userType ||
    "student";

  const studentRole =
    rawUserType === "student"
      ? "Student"
      : String(rawUserType)
          .replace(/_/g, " ")
          .replace(/\b\w/g, (letter) =>
            letter.toUpperCase()
          );

  const focusAreas = [
    {
      icon: <CrownOutlined />,
      title: "Leadership Development",
      text:
        "Build the confidence, discipline, and practical leadership skills needed to lead people and organizations effectively.",
      color: colors.gold,
    },
    {
      icon: <TeamOutlined />,
      title: "Team & People",
      text:
        "Develop stronger communication, collaboration, delegation, and relationship-building skills.",
      color: colors.teal,
    },
    {
      icon: <AimOutlined />,
      title: "Strategic Thinking",
      text:
        "Learn how to analyze situations, establish priorities, make informed decisions, and pursue meaningful goals.",
      color: colors.sky,
    },
    {
      icon: <RocketOutlined />,
      title: "Personal Growth",
      text:
        "Strengthen self-awareness, professionalism, responsibility, initiative, and continuous learning.",
      color: colors.orange,
    },
  ];

  const principles = [
    "Leadership through responsibility and service",
    "Professional communication and collaboration",
    "Practical learning and real-world application",
    "Integrity, discipline, and accountability",
    "Continuous personal and professional development",
  ];

  const journey = [
    {
      number: "01",
      title: "Learn",
      text:
        "Engage with structured leadership courses, lessons, activities, and learning resources.",
    },
    {
      number: "02",
      title: "Practice",
      text:
        "Apply leadership concepts through exercises, assignments, discussions, and practical activities.",
    },
    {
      number: "03",
      title: "Develop",
      text:
        "Strengthen your leadership mindset, communication, decision-making, and professional skills.",
    },
    {
      number: "04",
      title: "Lead",
      text:
        "Use what you have learned to contribute positively to teams, organizations, communities, and society.",
    },
  ];

  return (
    <div
      className={`cayep-student-overview ${
        darkMode ? "student-dark" : ""
      }`}
      style={{
        width: "100%",
        maxWidth: 1800,
        margin: "0 auto",
        padding: "0 2px 20px",
        boxSizing: "border-box",
      }}
    >
      <motion.div
        initial={{
          opacity: 0,
          y: 8,
        }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          duration: 0.35,
        }}
      >
        <Card
          bordered={false}
          style={{
            width: "100%",
            marginBottom: 15,
            borderRadius: 18,
            overflow: "hidden",
            background: colors.card,
            border:
              `1px solid ${colors.border}`,
            boxShadow:
              "0 8px 28px rgba(6,42,75,.055)",
          }}
          styles={{
            body: {
              padding: 0,
            },
          }}
        >
          <div className="cayep-welcome-banner">

            <div className="cayep-banner-glow cayep-banner-glow-one" />

            <div className="cayep-banner-glow cayep-banner-glow-two" />

            <div className="cayep-welcome-content">

              <div className="cayep-welcome-profile-area">

                <div className="cayep-welcome-profile-image">
                  <ProfileImage
                    theme={theme}
                    overview
                  />
                </div>

                <div className="cayep-student-details">

                  <span className="cayep-student-welcome">
                    WELCOME BACK
                  </span>

                  <span className="cayep-student-name">
                    {studentName}
                  </span>

                  <div className="cayep-student-meta">

                    <span className="cayep-student-meta-item">
                      <UserOutlined />
                      <span className="cayep-student-meta-text">
                        {studentRole}
                      </span>
                    </span>

                    {studentEmail && (
                      <span className="cayep-student-meta-item">
                        <MailOutlined />
                        <span className="cayep-student-meta-text">
                          {studentEmail}
                        </span>
                      </span>
                    )}

                  </div>

                </div>

              </div>

              <div className="cayep-welcome-copy">

                <Text className="cayep-welcome-eyebrow">
                  CAYEP • LEADERSHIP TRAINING
                </Text>

                <Title className="cayep-welcome-title">
                  Welcome to CAYEP Leadership Training
                </Title>

                <Paragraph className="cayep-welcome-description">
                  A professional learning environment designed
                  to develop capable, responsible, confident, and
                  purpose-driven leaders through practical
                  education and continuous personal development.
                </Paragraph>

              </div>

            </div>

            <div className="cayep-welcome-mark">
              <CrownOutlined />
            </div>

          </div>
        </Card>

        <Row
          gutter={[14, 14]}
          style={{
            margin: 0,
          }}
        >
          <Col
            xs={24}
            xl={15}
            style={{
              paddingLeft: 7,
              paddingRight: 7,
            }}
          >
            <motion.div
              initial={{
                opacity: 0,
                y: 8,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 0.06,
                duration: 0.3,
              }}
              style={{
                height: "100%",
              }}
            >
              <Card
                bordered={false}
                style={{
                  height: "100%",
                  minHeight: 275,
                  borderRadius: 17,
                  background: colors.card,
                  border:
                    `1px solid ${colors.border}`,
                  boxShadow:
                    "0 7px 24px rgba(6,42,75,.045)",
                }}
                styles={{
                  body: {
                    padding: 0,
                  },
                }}
              >
                <div
                  className="cayep-section-header"
                  style={{
                    borderBottom:
                      `1px solid ${colors.border}`,
                  }}
                >
                  <div>
                    <Text
                      className="cayep-section-eyebrow"
                      style={{
                        color: colors.teal,
                      }}
                    >
                      About CAYEP
                    </Text>

                    <Title
                      level={4}
                      style={{
                        color: colors.text,
                      }}
                    >
                      Developing Leaders for Meaningful Impact
                    </Title>
                  </div>

                  <div
                    className="cayep-section-icon"
                    style={{
                      color: colors.deepBlue,
                      background: darkMode
                        ? "rgba(15,109,171,.14)"
                        : "#F1F7FC",
                    }}
                  >
                    <GlobalOutlined />
                  </div>
                </div>

                <div className="cayep-section-body">
                  <Paragraph
                    style={{
                      color: colors.muted,
                      marginBottom: 13,
                    }}
                  >
                    CAYEP Leadership Training provides a
                    structured environment where learners
                    can develop the knowledge, attitudes,
                    and practical abilities required for
                    effective leadership.
                  </Paragraph>

                  <Paragraph
                    style={{
                      color: colors.muted,
                      marginBottom: 0,
                    }}
                  >
                    The training experience combines
                    leadership education, practical
                    activities, communication, teamwork,
                    personal development, and responsible
                    decision-making to help learners
                    prepare for leadership opportunities
                    in their professional and community
                    lives.
                  </Paragraph>

                  <div className="cayep-info-strip">
                    <div className="cayep-info-strip-icon">
                      <SafetyOutlined />
                    </div>

                    <div>
                      <Text
                        strong
                        style={{
                          color: colors.text,
                          fontSize: 12,
                        }}
                      >
                        Leadership with Purpose
                      </Text>

                      <Text
                        style={{
                          display: "block",
                          color: colors.muted,
                          fontSize: 11,
                          marginTop: 2,
                        }}
                      >
                        Learn, grow, serve, and lead
                        with integrity.
                      </Text>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </Col>

          <Col
            xs={24}
            xl={9}
            style={{
              paddingLeft: 7,
              paddingRight: 7,
            }}
          >
            <motion.div
              initial={{
                opacity: 0,
                y: 8,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 0.1,
                duration: 0.3,
              }}
              style={{
                height: "100%",
              }}
            >
              <Card
                bordered={false}
                style={{
                  height: "100%",
                  minHeight: 275,
                  borderRadius: 17,
                  background: colors.card,
                  border:
                    `1px solid ${colors.border}`,
                  boxShadow:
                    "0 7px 24px rgba(6,42,75,.045)",
                }}
                styles={{
                  body: {
                    padding: 0,
                  },
                }}
              >
                <div
                  className="cayep-section-header"
                  style={{
                    borderBottom:
                      `1px solid ${colors.border}`,
                  }}
                >
                  <div>
                    <Text
                      className="cayep-section-eyebrow"
                      style={{
                        color: colors.orange,
                      }}
                    >
                      Training Philosophy
                    </Text>

                    <Title
                      level={4}
                      style={{
                        color: colors.text,
                      }}
                    >
                      Our Principles
                    </Title>
                  </div>

                  <div
                    className="cayep-section-icon"
                    style={{
                      color: colors.orange,
                      background: darkMode
                        ? "rgba(192,117,13,.14)"
                        : "#FCF7EF",
                    }}
                  >
                    <ReadOutlined />
                  </div>
                </div>

                <div className="cayep-section-body">
                  <div className="cayep-principles">
                    {principles.map(
                      (principle, index) => (
                        <div
                          className="cayep-principle"
                          key={index}
                        >
                          <CheckCircleOutlined
                            style={{
                              color: colors.green,
                            }}
                          />

                          <Text
                            style={{
                              color: colors.muted,
                            }}
                          >
                            {principle}
                          </Text>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </Card>
            </motion.div>
          </Col>
        </Row>

        <motion.div
          initial={{
            opacity: 0,
            y: 8,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            delay: 0.14,
            duration: 0.3,
          }}
        >
          <Card
            bordered={false}
            style={{
              marginTop: 14,
              borderRadius: 17,
              background: colors.card,
              border:
                `1px solid ${colors.border}`,
              boxShadow:
                "0 7px 24px rgba(6,42,75,.045)",
            }}
            styles={{
              body: {
                padding: 0,
              },
            }}
          >
            <div
              className="cayep-section-header"
              style={{
                borderBottom:
                  `1px solid ${colors.border}`,
              }}
            >
              <div>
                <Text
                  className="cayep-section-eyebrow"
                  style={{
                    color: colors.sky,
                  }}
                >
                  Learning Areas
                </Text>

                <Title
                  level={4}
                  style={{
                    color: colors.text,
                  }}
                >
                  Explore Your Leadership Potential
                </Title>
              </div>

              <div
                className="cayep-section-icon"
                style={{
                  color: colors.sky,
                  background: darkMode
                    ? "rgba(15,109,171,.14)"
                    : "#F1F7FC",
                }}
              >
                <BookOutlined />
              </div>
            </div>

            <div
              style={{
                padding: 14,
              }}
            >
              <Row gutter={[12, 12]}>
                {focusAreas.map(
                  (area, index) => (
                    <Col
                      xs={24}
                      sm={12}
                      xl={6}
                      key={index}
                    >
                      <motion.div
                        whileHover={{
                          y: -2,
                        }}
                        transition={{
                          duration: 0.16,
                        }}
                        className="cayep-focus-card"
                        style={{
                          border:
                            `1px solid ${colors.border}`,
                          background:
                            colors.soft,
                        }}
                      >
                        <div
                          className="cayep-focus-icon"
                          style={{
                            color: area.color,
                            background:
                              darkMode
                                ? `${area.color}18`
                                : `${area.color}12`,
                          }}
                        >
                          {area.icon}
                        </div>

                        <Title
                          level={5}
                          style={{
                            color: colors.text,
                          }}
                        >
                          {area.title}
                        </Title>

                        <Text
                          style={{
                            color: colors.muted,
                          }}
                        >
                          {area.text}
                        </Text>
                      </motion.div>
                    </Col>
                  )
                )}
              </Row>
            </div>
          </Card>
        </motion.div>

        <Row
          gutter={[14, 14]}
          style={{
            marginTop: 14,
            marginLeft: 0,
            marginRight: 0,
          }}
        >
          <Col
            xs={24}
            lg={14}
            style={{
              paddingLeft: 7,
              paddingRight: 7,
            }}
          >
            <motion.div
              initial={{
                opacity: 0,
                y: 8,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 0.18,
                duration: 0.3,
              }}
              style={{
                height: "100%",
              }}
            >
              <Card
                bordered={false}
                style={{
                  height: "100%",
                  borderRadius: 17,
                  background: colors.card,
                  border:
                    `1px solid ${colors.border}`,
                  boxShadow:
                    "0 7px 24px rgba(6,42,75,.045)",
                }}
                styles={{
                  body: {
                    padding: 0,
                  },
                }}
              >
                <div
                  className="cayep-section-header"
                  style={{
                    borderBottom:
                      `1px solid ${colors.border}`,
                  }}
                >
                  <div>
                    <Text
                      className="cayep-section-eyebrow"
                      style={{
                        color: colors.gold,
                      }}
                    >
                      Your Development Journey
                    </Text>

                    <Title
                      level={4}
                      style={{
                        color: colors.text,
                      }}
                    >
                      Learn. Practice. Develop. Lead.
                    </Title>
                  </div>

                  <div
                    className="cayep-section-icon"
                    style={{
                      color: colors.orange,
                      background: darkMode
                        ? "rgba(246,201,68,.12)"
                        : "#FFF9E8",
                    }}
                  >
                    <RocketOutlined />
                  </div>
                </div>

                <div className="cayep-journey">
                  {journey.map(
                    (item, index) => (
                      <div
                        className="cayep-journey-item"
                        key={item.number}
                      >
                        <div
                          className="cayep-journey-number"
                          style={{
                            color:
                              index % 2 === 0
                                ? colors.deepBlue
                                : colors.orange,
                            background:
                              darkMode
                                ? "#202D43"
                                : "#F5F8FC",
                          }}
                        >
                          {item.number}
                        </div>

                        <div className="cayep-journey-content">
                          <Text
                            strong
                            style={{
                              color: colors.text,
                            }}
                          >
                            {item.title}
                          </Text>

                          <Text
                            style={{
                              display: "block",
                              color: colors.muted,
                              marginTop: 3,
                            }}
                          >
                            {item.text}
                          </Text>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </Card>
            </motion.div>
          </Col>

          <Col
            xs={24}
            lg={10}
            style={{
              paddingLeft: 7,
              paddingRight: 7,
            }}
          >
            <motion.div
              initial={{
                opacity: 0,
                y: 8,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 0.22,
                duration: 0.3,
              }}
              style={{
                height: "100%",
              }}
            >
              <div
                className="cayep-cta-card"
                style={{
                  height: "100%",
                  background:
                    `linear-gradient(135deg, ${colors.navy} 0%, ${colors.deepBlue} 58%, ${colors.sky} 100%)`,
                }}
              >
                <div className="cayep-cta-pattern" />

                <div className="cayep-cta-content">
                  <div className="cayep-cta-icon">
                    <TeamOutlined />
                  </div>

                  <Text className="cayep-cta-eyebrow">
                    CAYEP LEADERSHIP TRAINING
                  </Text>

                  <Title className="cayep-cta-title">
                    Your leadership journey starts here.
                  </Title>

                  <Paragraph className="cayep-cta-text">
                    Take advantage of every lesson, activity,
                    assignment, and learning opportunity
                    available through the CAYEP training
                    environment.
                  </Paragraph>

                  <div className="cayep-cta-footer">
                    <span>
                      Knowledge
                    </span>

                    <span className="cayep-cta-dot">
                      •
                    </span>

                    <span>
                      Character
                    </span>

                    <span className="cayep-cta-dot">
                      •
                    </span>

                    <span>
                      Leadership
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </Col>
        </Row>
      </motion.div>

      <style>
        {`
          .cayep-student-overview {
            --overview-radius: 17px;
          }

          .cayep-welcome-banner {
            position: relative;
            min-height: 205px;
            overflow: hidden;
            padding: 23px 25px;
            display: flex;
            align-items: center;
            background:
              linear-gradient(
                135deg,
                #062A4B 0%,
                #063A6B 54%,
                #0F6DAB 100%
              );
          }

          .cayep-banner-glow {
            position: absolute;
            border-radius: 50%;
            pointer-events: none;
          }

          .cayep-banner-glow-one {
            width: 280px;
            height: 280px;
            right: -95px;
            top: -170px;
            background:
              rgba(246,201,68,.12);
          }

          .cayep-banner-glow-two {
            width: 220px;
            height: 220px;
            left: 44%;
            bottom: -185px;
            background:
              rgba(22,122,137,.20);
          }

          .cayep-welcome-content {
            position: relative;
            z-index: 2;
            width: 100%;
            display: flex;
            align-items: center;
            gap: 25px;
          }

          .cayep-welcome-profile-area {
            flex: 0 1 auto;
            min-width: 245px;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 15px;
          }

          .cayep-welcome-profile-image {
            flex: 0 0 auto;
            width: auto;
            min-width: 0;
            height: auto;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: visible;
            position: relative;
            line-height: 0;
          }

          .cayep-welcome-profile-image > * {
            max-width: 100%;
          }

          .cayep-student-details {
            min-width: 0;
            max-width: 210px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            text-align: left;
          }

          .cayep-student-welcome {
            display: block;
            margin-bottom: 3px;
            color: #F6C944;
            font-size: 9px;
            line-height: 1.2;
            font-weight: 800;
            letter-spacing: 1.15px;
          }

          .cayep-student-name {
            display: block;
            width: 100%;
            color: #FFFFFF;
            font-size: 18px;
            line-height: 1.25;
            font-weight: 800;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .cayep-student-meta {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 3px;
            margin-top: 6px;
          }

          .cayep-student-meta-item {
            width: 100%;
            min-width: 0;
            display: flex;
            align-items: center;
            gap: 5px;
            color: rgba(240,247,255,.76);
            font-size: 9.5px;
            line-height: 1.35;
          }

          .cayep-student-meta-text {
            min-width: 0;
            max-width: 100%;
            overflow-wrap: anywhere;
            word-break: break-word;
          }

          .cayep-student-meta-item .anticon {
            color: #F6C944;
            font-size: 9px;
            flex: 0 0 auto;
          }

          .cayep-welcome-copy {
            min-width: 0;
            max-width: 780px;
            flex: 1;
          }

          .cayep-welcome-eyebrow {
            display: block;
            margin-bottom: 5px;
            color: #F6C944 !important;
            font-size: 10px;
            font-weight: 800;
            letter-spacing: 1.35px;
            text-transform: uppercase;
          }

          .cayep-welcome-title {
            margin: 0 !important;
            color: #FFFFFF !important;
            font-size: 26px !important;
            line-height: 1.16 !important;
            font-weight: 800 !important;
            letter-spacing: -.45px;
          }

          .cayep-welcome-description {
            max-width: 700px;
            margin: 8px 0 0 !important;
            color:
              rgba(240,247,255,.82) !important;
            font-size: 12px;
            line-height: 1.6;
          }

          .cayep-welcome-mark {
            position: absolute;
            right: 30px;
            bottom: 20px;
            z-index: 2;
            width: 52px;
            height: 52px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 15px;
            color: rgba(246,201,68,.90);
            background:
              rgba(255,255,255,.065);
            border:
              1px solid rgba(255,255,255,.11);
            font-size: 23px;
            backdrop-filter: blur(8px);
          }

          .cayep-section-header {
            min-height: 69px;
            padding: 14px 17px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
          }

          .cayep-section-eyebrow {
            display: block;
            margin-bottom: 3px;
            font-size: 9px;
            line-height: 1.1;
            font-weight: 800;
            letter-spacing: .95px;
            text-transform: uppercase;
          }

          .cayep-section-header .ant-typography {
            margin-bottom: 0 !important;
          }

          .cayep-section-header h4 {
            margin-top: 0 !important;
            font-size: 17px !important;
            line-height: 1.2 !important;
            font-weight: 800 !important;
          }

          .cayep-section-icon {
            width: 37px;
            height: 37px;
            min-width: 37px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
          }

          .cayep-section-body {
            padding: 15px 17px 17px;
          }

          .cayep-section-body p {
            font-size: 12px;
            line-height: 1.65;
          }

          .cayep-info-strip {
            margin-top: 15px;
            padding: 10px 11px;
            display: flex;
            align-items: center;
            gap: 10px;
            border-radius: 10px;
            background:
              rgba(15,109,171,.055);
            border:
              1px solid rgba(15,109,171,.10);
          }

          .cayep-info-strip-icon {
            width: 31px;
            height: 31px;
            min-width: 31px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 8px;
            color: #0F6DAB;
            background:
              rgba(15,109,171,.10);
          }

          .cayep-principles {
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .cayep-principle {
            display: flex;
            align-items: flex-start;
            gap: 9px;
            font-size: 11px;
            line-height: 1.45;
          }

          .cayep-principle .anticon {
            margin-top: 2px;
            flex: 0 0 auto;
          }

          .cayep-focus-card {
            min-height: 170px;
            padding: 15px;
            border-radius: 13px;
            transition:
              transform 160ms ease,
              box-shadow 160ms ease,
              border-color 160ms ease;
          }

          .cayep-focus-card:hover {
            box-shadow:
              0 8px 22px rgba(6,42,75,.07);
          }

          .cayep-focus-icon {
            width: 38px;
            height: 38px;
            margin-bottom: 11px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 17px;
          }

          .cayep-focus-card h5 {
            margin: 0 0 7px !important;
            font-size: 13px !important;
            line-height: 1.25 !important;
            font-weight: 800 !important;
          }

          .cayep-focus-card > span {
            display: block;
            font-size: 10.5px;
            line-height: 1.55;
          }

          .cayep-journey {
            padding: 14px 17px 17px;
            display: grid;
            grid-template-columns:
              repeat(2, minmax(0, 1fr));
            gap: 0;
          }

          .cayep-journey-item {
            min-height: 92px;
            padding: 13px 14px;
            display: flex;
            align-items: flex-start;
            gap: 11px;
            border-bottom:
              1px solid rgba(225,230,239,.70);
          }

          .cayep-journey-item:nth-child(odd) {
            border-right:
              1px solid rgba(225,230,239,.70);
          }

          .cayep-journey-item:nth-child(3),
          .cayep-journey-item:nth-child(4) {
            border-bottom: 0;
          }

          .cayep-journey-number {
            width: 35px;
            height: 35px;
            min-width: 35px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 9px;
            font-size: 10px;
            font-weight: 900;
          }

          .cayep-journey-content {
            min-width: 0;
            font-size: 10.5px;
            line-height: 1.5;
          }

          .cayep-cta-card {
            position: relative;
            min-height: 275px;
            padding: 24px;
            overflow: hidden;
            border-radius: 17px;
            box-shadow:
              0 10px 28px rgba(6,42,75,.12);
          }

          .cayep-cta-pattern {
            position: absolute;
            inset: 0;
            opacity: .12;
            pointer-events: none;
            background:
              repeating-linear-gradient(
                125deg,
                transparent 0,
                transparent 55px,
                rgba(255,255,255,.25) 56px,
                rgba(255,255,255,.25) 57px,
                transparent 58px,
                transparent 112px
              );
          }

          .cayep-cta-content {
            position: relative;
            z-index: 2;
          }

          .cayep-cta-icon {
            width: 42px;
            height: 42px;
            margin-bottom: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 11px;
            color: #F6C944;
            background:
              rgba(255,255,255,.09);
            border:
              1px solid rgba(255,255,255,.13);
            font-size: 18px;
          }

          .cayep-cta-eyebrow {
            display: block;
            color: #F6C944 !important;
            font-size: 9px;
            font-weight: 800;
            letter-spacing: 1.15px;
          }

          .cayep-cta-title {
            max-width: 360px;
            margin: 6px 0 0 !important;
            color: #FFFFFF !important;
            font-size: 22px !important;
            line-height: 1.22 !important;
            font-weight: 800 !important;
          }

          .cayep-cta-text {
            max-width: 390px;
            margin: 10px 0 0 !important;
            color:
              rgba(240,247,255,.78) !important;
            font-size: 11px;
            line-height: 1.6;
          }

          .cayep-cta-footer {
            margin-top: 20px;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 7px;
            color:
              rgba(255,255,255,.88);
            font-size: 10px;
            font-weight: 700;
            letter-spacing: .3px;
          }

          .cayep-cta-dot {
            color: #F6C944;
          }

          .student-dark .cayep-journey-item {
            border-color:
              rgba(43,57,81,.75);
          }

          .student-dark .cayep-info-strip {
            background:
              rgba(15,109,171,.08);
            border-color:
              rgba(15,109,171,.16);
          }

          @media (max-width: 1200px) {
            .cayep-welcome-content {
              gap: 18px;
            }

            .cayep-welcome-profile-area {
              min-width: 225px;
            }

            .cayep-welcome-title {
              font-size: 23px !important;
            }

            .cayep-welcome-mark {
              right: 22px;
            }
          }

          @media (max-width: 991px) {
            .cayep-welcome-banner {
              min-height: auto;
            }

            .cayep-welcome-content {
              align-items: center;
              flex-direction: column;
              gap: 20px;
              text-align: center;
            }

            .cayep-welcome-profile-area {
              width: 100%;
              min-width: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 16px;
            }

            .cayep-student-details {
              max-width: 280px;
              align-items: flex-start;
              text-align: left;
            }

            .cayep-welcome-copy {
              width: 100%;
              max-width: 850px;
              text-align: center;
            }

            .cayep-welcome-eyebrow {
              text-align: center;
            }

            .cayep-welcome-title {
              text-align: center;
            }

            .cayep-welcome-description {
              margin-left: auto !important;
              margin-right: auto !important;
              text-align: center;
            }

            .cayep-welcome-mark {
              display: none;
            }
          }

          @media (max-width: 767px) {
            .cayep-welcome-banner {
              padding: 22px 17px 23px;
            }

            .cayep-welcome-content {
              gap: 22px;
            }

            .cayep-welcome-profile-area {
              flex-direction: column;
              width: 100%;
              gap: 9px;
            }

            .cayep-welcome-profile-image {
              width: auto;
              align-self: center;
              justify-content: center;
            }

            .cayep-student-details {
              width: 100%;
              max-width: 100%;
              align-items: center;
              text-align: center;
            }

            .cayep-student-welcome {
              margin-bottom: 4px;
              text-align: center;
              font-size: 8.5px;
              letter-spacing: 1.1px;
            }

            .cayep-student-name {
              width: 100%;
              max-width: 100%;
              text-align: center;
              white-space: normal;
              overflow: visible;
              text-overflow: clip;
              overflow-wrap: anywhere;
              word-break: break-word;
              font-size: 17px;
              line-height: 1.25;
            }

            .cayep-student-meta {
              width: 100%;
              margin-top: 7px;
              align-items: center;
              justify-content: center;
              gap: 4px;
            }

            .cayep-student-meta-item {
              width: auto;
              max-width: 100%;
              justify-content: center;
              text-align: center;
              font-size: 8.8px;
            }

            .cayep-student-meta-text {
              max-width: calc(100vw - 70px);
              overflow-wrap: anywhere;
              word-break: break-word;
            }

            .cayep-welcome-copy {
              width: 100%;
            }

            .cayep-welcome-eyebrow {
              font-size: 8.5px;
              letter-spacing: 1px;
              line-height: 1.4;
            }

            .cayep-welcome-title {
              font-size: 20px !important;
              line-height: 1.22 !important;
              text-align: center;
            }

            .cayep-welcome-description {
              max-width: 620px;
              font-size: 10.8px;
              line-height: 1.55;
              text-align: center;
            }

            .cayep-section-header {
              padding: 13px 14px;
            }

            .cayep-section-body {
              padding: 13px 14px 15px;
            }

            .cayep-journey {
              grid-template-columns: 1fr;
              padding: 10px 12px 12px;
            }

            .cayep-journey-item {
              min-height: auto;
              padding: 12px 5px;
              border-right: 0 !important;
              border-bottom:
                1px solid rgba(225,230,239,.70);
            }

            .cayep-journey-item:last-child {
              border-bottom: 0;
            }

            .cayep-cta-card {
              min-height: 250px;
              padding: 21px;
            }
          }

          @media (max-width: 480px) {
            .cayep-welcome-banner {
              padding: 20px 13px 22px;
            }

            .cayep-welcome-content {
              gap: 20px;
            }

            .cayep-welcome-profile-area {
              gap: 8px;
            }

            .cayep-student-welcome {
              font-size: 8px;
              letter-spacing: 1px;
            }

            .cayep-student-name {
              max-width: 100%;
              padding: 0 4px;
              font-size: 15px;
            }

            .cayep-student-meta {
              margin-top: 6px;
              gap: 3px;
            }

            .cayep-student-meta-item {
              font-size: 8px;
              line-height: 1.35;
            }

            .cayep-student-meta-text {
              max-width: calc(100vw - 55px);
            }

            .cayep-welcome-eyebrow {
              font-size: 7.8px;
              letter-spacing: .85px;
            }

            .cayep-welcome-title {
              font-size: 17px !important;
              line-height: 1.25 !important;
            }

            .cayep-welcome-description {
              margin-top: 6px !important;
              font-size: 10px;
              line-height: 1.55;
            }

            .cayep-section-header h4 {
              font-size: 15px !important;
            }

            .cayep-section-icon {
              width: 33px;
              height: 33px;
              min-width: 33px;
              font-size: 14px;
            }

            .cayep-focus-card {
              min-height: auto;
            }

            .cayep-cta-title {
              font-size: 19px !important;
            }
          }

          @media (max-width: 360px) {
            .cayep-welcome-banner {
              padding-left: 10px;
              padding-right: 10px;
            }

            .cayep-student-name {
              font-size: 14px;
            }

            .cayep-student-meta-item {
              font-size: 7.5px;
            }

            .cayep-welcome-title {
              font-size: 16px !important;
            }

            .cayep-welcome-eyebrow {
              font-size: 7.2px;
              letter-spacing: .7px;
            }

            .cayep-welcome-description {
              font-size: 9.5px;
            }
          }
        `}
      </style>
    </div>
  );
};

export default StudentOverview;